import { FlexRow } from '@/components/common/Flex';
import { FlexProps } from '@chakra-ui/react';
import bankImage from '/public/images/add-account/art-bank.png';
import Image, { StaticImageData } from 'next/image';
import { Button } from '../common/Button';
import { FlexCol } from '../common/Flex';
import { PlusCircle } from '../common/icons';
import Icon from '../common/icons/Icon';
import { hoverStyleProps } from '../common/styleHelpers';
import { Header, Paragraph, Subheader, Text } from '../common/Typography';

export interface ConnectionTypeViewProps extends FlexProps {
    onAddManually: () => void;
    onConnectAccount: () => void;
    isInRetirementContext: boolean;
}

const ConnectionTypeView = ({
    onAddManually,
    onConnectAccount,
    isInRetirementContext,
    ...props
}: ConnectionTypeViewProps) => {
    if (isInRetirementContext) {
        return (
            <FlexCol gap={4} pb={4} {...props}>
                <Header mb={8}>Add a new retirement account</Header>
                <ConnectionTypeOption
                    title="Connect a new account"
                    description="Banks, investments, or any other accounts that you’re planning to use in retirement."
                    image={bankImage}
                    onClick={onConnectAccount}
                />
                <ManualOption onAddManually={onAddManually} />
                <EncryptionNotice />
            </FlexCol>
        );
    }

    return (
        <FlexCol gap={4} pb={4} {...props}>
            <ConnectionTypeOption
                title="Connect your account"
                description="Connect over 15,000 financial institutions across bank accounts, cards, investment, retirement and more."
                image={bankImage}
                onClick={onConnectAccount}
            />
            <ManualOption onAddManually={onAddManually} />
            <EncryptionNotice />
        </FlexCol>
    );
};

const ConnectionTypeOption = ({
    title,
    description,
    image,
    onClick,
}: {
    title: string;
    description: string;
    image: StaticImageData;
    onClick: VoidFunction;
}) => {
    return (
        <FlexRow
            alignItems="center"
            justifyContent="space-between"
            onClick={onClick}
            {...hoverStyleProps({})}
            px={2}
            py={4}
            gap={4}
            borderWidth={1}
            rounded="xl"
            boxShadow="sm"
        >
            <Image
                src={image.src}
                alt={title}
                width={100}
                height={100}
                blurDataURL={image.blurDataURL}
            />
            <FlexCol pr={2}>
                <Subheader mb={2}>{title}</Subheader>
                <Paragraph textColor="textMuted">{description}</Paragraph>
            </FlexCol>
        </FlexRow>
    );
};

const ManualOption = ({ onAddManually }: { onAddManually: () => void }) => {
    return (
        <FlexRow>
            <Button
                variant="outline"
                size="sm"
                colorScheme="gray"
                leftIcon={<PlusCircle />}
                onClick={onAddManually}
                onClickEvent={'Add Account Manually Button Clicked'}
            >
                <Text fontSize="md">Add account manually</Text>
            </Button>
        </FlexRow>
    );
};

const EncryptionNotice = () => {
    return (
        <FlexRow mt={8} bg="gray.100" rounded="lg" alignSelf="center" p={2}>
            <Paragraph fontSize="sm">
                <Icon name="Lock01" mr={2} />
                We use bank-level AES-256 encryption to secure your data.
            </Paragraph>
        </FlexRow>
    );
};

export default ConnectionTypeView;
